import { useRef, useState } from "react";
import { Camera, X, Loader2 } from "lucide-react";
import { uploadFile, mediaSrc, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ImageUploader({ value = [], onChange, max = 5, testid = "image-uploader" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const remaining = max - value.length;
    const selected = files.slice(0, Math.max(0, remaining));
    setBusy(true);
    try {
      // Compress in browser, then upload as a Blob → object storage. We never
      // hold all base64 strings in memory at once and we never round-trip the
      // raw bytes through MongoDB.
      const urls = [];
      for (const f of selected) {
        const blob = await compressImageToBlob(f, 1280);
        const url = await uploadFile(
          new File([blob], (f.name || "photo").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" }),
          "product_image",
        );
        urls.push(url);
      }
      onChange([...value, ...urls]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'envoi des images");
    } finally {
      setBusy(false);
    }
  };

  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div data-testid={testid}>
      <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={onPick} />
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {value.map((img, i) => (
          <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-[#E8E6E1] group">
            <img src={mediaSrc(img)} alt="" className="h-full w-full object-cover" />
            <button
              type="button"
              onClick={() => remove(i)}
              className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
              data-testid={`remove-image-${i}`}
            >
              <X className="h-3 w-3" />
            </button>
            {i === 0 && (
              <div className="absolute bottom-1 left-1 bg-[#E65100] text-white text-[9px] font-bold px-1.5 py-0.5 rounded">PRINCIPALE</div>
            )}
          </div>
        ))}
        {value.length < max && (
          <button
            type="button"
            data-testid="upload-image-btn"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            className="aspect-square rounded-xl border-2 border-dashed border-[#E8E6E1] hover:border-[#E65100] flex flex-col items-center justify-center gap-1 text-[#73706B] hover:text-[#E65100] transition disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <Camera className="h-6 w-6" />}
            <span className="text-[10px] font-semibold">{busy ? "Envoi…" : "Ajouter"}</span>
          </button>
        )}
      </div>
      <div className="mt-2 text-[11px] text-[#73706B]">{value.length}/{max} images · La 1ère sera utilisée comme image principale</div>
    </div>
  );
}

// Resize + JPEG-compress an image client-side and return a Blob ready to
// upload. Keeps memory low (no base64 round-trip) and produces ~1 MB files
// from typical 4 MP phone photos.
async function compressImageToBlob(file, maxDim = 1280) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => {
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width > height) { height *= maxDim / width; width = maxDim; }
          else { width *= maxDim / height; height = maxDim; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(b => b ? resolve(b) : reject(new Error("compress failed")), "image/jpeg", 0.82);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
